/*
  UsageLog.js

  Component for generating and displaying the Usage Log content for
  Customer Contact Admin with Usage Log Access enabled.
*/

// NPM
import React, { useState, useEffect } from 'react';
import {
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  InputLabel,
  Box,
} from '@mui/material';
import { DateTime } from 'luxon';

// COMPONENTS
import DateRangeSelector from '../../common/DateRangeSelector';
import Paginator from '../../common/Paginator';

// CONFIG
import config from '../../../Config';

// NETWORKING
import useCostCentreReportRequest from '../../../requests/CostCentreReportRequest';

// I18N
import CostCentreReportsFormattedMessages from '../../../messages/reports/CostCentreReportsFormattedMessages';

const UsageLog = ({ customer, errorHandler, accessLevel, accessType }) => {
  const [data, setData] = useState([]);
  const [customer_id, setCustomerId] = useState(null);
  const [user_email, setUserEmail] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [search, setSearch] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageTotal, setPageTotal] = useState(0);
  const [pdfPassword, setPdfPassword] = useState('');
  const { fetchUsageLogData, generateUsageLog } = useCostCentreReportRequest();

  useEffect(() => {
    if (customer && customer !== null) {
      setCustomerId(customer.customer_id);
    }
  }, [customer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'user_email') setUserEmail(value);
    if (name === 'pdfPassword') setPdfPassword(value);
  };

  const handlePageSearch = async () => {
    setIsSearching(true);
    setData([]);
    setSearch(false);

    const searchParams = {
      fromDate: fromDate,
      toDate: toDate,
      pageNumber: pageNumber,
      pageLength: pageLength,
      userEmail: user_email,
    };

    try {
      const data = await fetchUsageLogData(searchParams, errorHandler);
      setData(data.results);
      setPageTotal(data.rowsCount);
      setIsSearching(false);
      setSearch(true);
    } catch (e) {
      console.error(e);
      setIsSearching(false);
    }
  };

  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
    handlePageSearch();
  };

  const changeEvent = (event, value) => {
    setPageNumber(value);
    handlePageSearch();
  };

  const searchUsageLog = async () => {
    setIsSearching(true);
    setData([]);
    setSearch(false);
    setPageNumber(1);

    const searchParams = {
      fromDate: fromDate,
      toDate: toDate,
      pageNumber: pageNumber,
      pageLength: pageLength,
      userEmail: user_email,
    };

    try {
      const data = await fetchUsageLogData(searchParams, errorHandler);
      setData(data.results);
      setPageTotal(data.rowsCount);
      setIsSearching(false);
      setSearch(true);
    } catch (e) {
      console.error(e);
      setIsSearching(false);
    }
  };

  const requestPdfReport = async () => {
    try {
      setIsSending(true);

      await generateUsageLog(
        fromDate,
        toDate,
        pdfPassword,
        user_email,
        errorHandler
      );
      setPdfPassword('');
      setIsSending(false);
    } catch (e) {
      console.error(e);
      setIsSending(false);
    }
  };

  const generateSearchTools = () => {
    return (
      <Stack sx={{ gap: 2, maxWidth: 670 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ md: 'center' }}
        >
          <InputLabel sx={{ minWidth: 170 }}>
            {CostCentreReportsFormattedMessages.searchByUserEmail}
          </InputLabel>

          <TextField
            type="text"
            name="user_email"
            value={user_email}
            onChange={handleInputChange}
            size="small"
            fullWidth
          />
        </Stack>

        <Stack>
          <DateRangeSelector
            rangeOnly={true}
            handleDateSearch={handlePageSearch}
            parentStartDate={fromDate}
            setParentStartDate={setFromDate}
            parentEndDate={toDate}
            setParentEndDate={setToDate}
          />
        </Stack>

        <Box sx={{ alignSelf: 'end' }}>
          <Button
            onClick={searchUsageLog}
            variant="contained"
            color="secondary"
          >
            {CostCentreReportsFormattedMessages.showReport}
          </Button>
        </Box>
      </Stack>
    );
  };

  const generatePdfPassword = () => {
    if (isSending) {
      return <Typography>Sending...</Typography>;
    }
    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          onChange={handleInputChange}
          type="password"
          name="pdfPassword"
          label={CostCentreReportsFormattedMessages.enterPassword}
          variant="outlined"
          size="small"
        />

        <Button
          className="searchRange"
          onClick={requestPdfReport}
          disabled={data.length === 0}
          variant="contained"
          color="secondary"
        >
          {CostCentreReportsFormattedMessages.emailPDFFile}
        </Button>
      </Box>
    );
  };

  const generateTable = () => {
    if (isSearching) {
      return (
        <Typography>{CostCentreReportsFormattedMessages.searching}</Typography>
      );
    } else if (data.length === 0 && search) {
      return (
        <Typography>{CostCentreReportsFormattedMessages.noResult}</Typography>
      );
    } else if (search) {
      return (
        <>
          {generatePdfPassword()}

          <TableContainer
            sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="right">
                    {CostCentreReportsFormattedMessages.key}
                  </TableCell>
                  <TableCell align="right">
                    {CostCentreReportsFormattedMessages.costCentre}
                  </TableCell>
                  <TableCell align="right">
                    {CostCentreReportsFormattedMessages.userName}
                  </TableCell>
                  <TableCell align="right">
                    {CostCentreReportsFormattedMessages.date}
                  </TableCell>
                  <TableCell align="right">
                    {CostCentreReportsFormattedMessages.time}
                  </TableCell>
                  <TableCell align="right">
                    {CostCentreReportsFormattedMessages.phone}
                  </TableCell>
                  <TableCell align="right">
                    {CostCentreReportsFormattedMessages.type}
                  </TableCell>
                  <TableCell align="right">
                    {CostCentreReportsFormattedMessages.quantity}
                  </TableCell>
                  <TableCell>
                    {CostCentreReportsFormattedMessages.text}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{generateTableRows()}</TableBody>
            </Table>
          </TableContainer>

          <Paginator
            itemCount={pageTotal}
            pageNumber={pageNumber}
            changeEvent={changeEvent}
            pageLength={pageLength}
            changePageLength={changePageLength}
          />
        </>
      );
    }
  };

  const generateTableRows = () => {
    const rows = [];
    for (let i = 0; i < data.length; i++) {
      const d = data[i];
      let smsText;
      let sentDate = DateTime.fromISO(d.sms_sent);
      let rezoned = sentDate.setZone(localStorage.getItem('timeShort'), {
        keepLocalTime: false,
      });
      const key = i + (pageNumber - 1) * pageLength;
      try {
        smsText = decodeURIComponent(d.sms_text);
      } catch (err) {
        smsText = 'ERROR: MALFORMED TEXT';
      }
      let destTn = 'N/A';
      if (d.dest_tn) {
        d.dest_tn = d.dest_tn.replace('+', '');
        destTn = `(+)${d.dest_tn}`;
      }
      rows.push(
        <TableRow key={'list' + d.sms_id} id={d.sms_id}>
          <TableCell align="right">{key}</TableCell>
          <TableCell align="right">{d.cost_centre_name}</TableCell>
          <TableCell align="right">{d.user_name}</TableCell>
          <TableCell align="right">{rezoned.toFormat('dd-LLL-yyyy')}</TableCell>
          <TableCell align="right">{rezoned.toFormat('h:mm a')}</TableCell>
          <TableCell align="right">{destTn}</TableCell>
          <TableCell align="right">{d.sms_type}</TableCell>
          <TableCell align="right">{d.no_of_sms_sent}</TableCell>
          <TableCell>{smsText}</TableCell>
        </TableRow>
      );
    }
    return rows;
  };

  if (accessLevel <= '0' || accessType === '0') {
    return null;
  }

  return (
    <>
      <Typography gutterBottom>
        {CostCentreReportsFormattedMessages.accountText}{' '}
        {customer ? customer.customer_name : ''}
      </Typography>

      {generateSearchTools()}
      {generateTable()}
    </>
  );
};

export default UsageLog;
